import { httpsCallable } from "firebase/functions";
import { addDoc, collection, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc, where, type DocumentData } from "firebase/firestore";
import type { CheckoutInput, Order, PrintFormat } from "./domain";
import { demoOrders } from "./demoData";
import { getFirebaseServices } from "./firebase";
import { adminRoles, type Session } from "./auth";

export const backendFunctionsEnabled = import.meta.env.VITE_ENABLE_BACKEND_FUNCTIONS === "true";

export async function submitCheckout(input: CheckoutInput): Promise<{ orderNumber: string }> {
  const services = getFirebaseServices();
  if (!services) {
    await new Promise((resolve) => window.setTimeout(resolve, 450));
    return { orderNumber: `DEMO-${String(Date.now()).slice(-6)}` };
  }
  if (!backendFunctionsEnabled) {
    const user = services.auth.currentUser;
    if (!user) throw new Error("Login diperlukan sebelum membuat pesanan.");
    if (!user.emailVerified) throw new Error("Verifikasi email sebelum membuat pesanan.");
    const date = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Jakarta", year: "2-digit", month: "2-digit", day: "2-digit" }).format(new Date()).replaceAll("-", "");
    const orderNumber = `AKR-${date}-${String(Date.now()).slice(-6)}`;
    const total = input.lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0);
    await addDoc(collection(services.db, "orders"), {
      number: orderNumber,
      customerUid: user.uid,
      customerEmail: user.email ?? "",
      customerName: input.name.trim(),
      customerPhone: input.phone.trim(),
      shippingAddress: input.address.trim(),
      customerType: input.audience,
      lines: input.lines,
      subtotal: total,
      shippingTotal: 0,
      discountTotal: 0,
      total,
      currency: "IDR",
      status: "new",
      paymentStatus: "unpaid",
      printStatus: "not_printed",
      priceReviewStatus: "pending_admin_confirmation",
      source: "spark_web_order",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { orderNumber };
  }
  const checkout = httpsCallable<CheckoutInput, { orderNumber: string }>(services.functions, "createCheckout");
  const response = await checkout(input);
  return response.data;
}

function mapOrder(id: string, data: DocumentData): Order {
  const createdAt = data.createdAt?.toDate?.() instanceof Date ? data.createdAt.toDate().toISOString() : new Date().toISOString();
  return {
    id,
    number: String(data.number ?? id),
    customerName: String(data.customerName ?? "Pelanggan"),
    customerEmail: String(data.customerEmail ?? ""),
    customerPhone: String(data.customerPhone ?? ""),
    shippingAddress: String(data.shippingAddress ?? ""),
    customerType: ["reseller", "partner"].includes(data.customerType) ? data.customerType : "customer",
    status: ["confirmed", "preparing", "ready", "completed"].includes(data.status) ? data.status : "new",
    paymentStatus: ["pending", "paid"].includes(data.paymentStatus) ? data.paymentStatus : "unpaid",
    priceReviewStatus: data.priceReviewStatus === "confirmed" ? "confirmed" : "pending_admin_confirmation",
    lines: Array.isArray(data.lines) ? data.lines.map((line: DocumentData) => ({
      productId: String(line.productId ?? ""),
      productName: String(line.productName ?? "Produk"),
      variant: String(line.variant ?? ""),
      quantity: Number(line.quantity ?? 0),
      unitPrice: Number(line.unitPrice ?? 0),
    })) : [],
    subtotal: Number(data.subtotal ?? data.total ?? 0),
    shippingTotal: Number(data.shippingTotal ?? 0),
    discountTotal: Number(data.discountTotal ?? 0),
    total: Number(data.total ?? 0),
    createdAt,
    printStatus: data.printStatus === "printed" ? "printed" : "not_printed",
  };
}

export async function loadOrders(session: Session | null): Promise<Order[]> {
  const services = getFirebaseServices();
  if (!services) return demoOrders;
  if (!session) return [];
  const ordersRef = collection(services.db, "orders");
  const constraints = adminRoles.includes(session.role)
    ? [orderBy("createdAt", "desc"), limit(100)]
    : [where("customerUid", "==", session.uid), orderBy("createdAt", "desc"), limit(50)];
  const snapshot = await getDocs(query(ordersRef, ...constraints));
  return snapshot.docs.map((item) => mapOrder(item.id, item.data()));
}

export async function queuePrintJob(orderId: string, format: PrintFormat): Promise<{ jobId: string }> {
  const services = getFirebaseServices();
  if (!services) return { jobId: `demo-print-${orderId}-${format}` };
  if (!backendFunctionsEnabled) {
    await updateDoc(doc(services.db, "orders", orderId), { printStatus: "printed", updatedAt: serverTimestamp() });
    return { jobId: `local-print-${orderId}-${format}` };
  }
  const createPrintJob = httpsCallable<{ orderId: string; format: PrintFormat }, { jobId: string }>(services.functions, "createPrintJob");
  const response = await createPrintJob({ orderId, format });
  return response.data;
}

export async function updateOrderState(orderId: string, input: Pick<Order, "status" | "paymentStatus" | "priceReviewStatus" | "subtotal" | "shippingTotal" | "discountTotal">): Promise<number> {
  const services = getFirebaseServices();
  const shippingTotal = Math.max(0, Number(input.shippingTotal) || 0);
  const discountTotal = Math.max(0, Math.min(Number(input.discountTotal) || 0, input.subtotal + shippingTotal));
  const total = input.subtotal + shippingTotal - discountTotal;
  if (!services) return total;
  await updateDoc(doc(services.db, "orders", orderId), {
    status: input.status,
    paymentStatus: input.paymentStatus,
    priceReviewStatus: input.priceReviewStatus,
    shippingTotal,
    discountTotal,
    total,
    updatedAt: serverTimestamp(),
  });
  return total;
}
